import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Camera,
  Send,
  Sparkles,
  Image as ImageIcon,
  Heart,
  Upload,
  X,
  MapPin,
  ShoppingBag,
  MessageCircle,
  Lock,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILES = 5;
const MAX_FILE_MB = 4;

const COLORS = [
  { name: "Pink", hex: "#FF3EA5" },
  { name: "Magenta", hex: "#FF2EC7" },
  { name: "Electric Blue", hex: "#33C5FF" },
  { name: "Deep Sea Blue", hex: "#0066FF" },
  { name: "Purple", hex: "#B026FF" },
  { name: "Warm White", hex: "#FFF2C2" },
  { name: "Pure White", hex: "#FFFFFF" },
  { name: "Cool White", hex: "#E8F0FF" },
  { name: "Ice White", hex: "#C8E0FF" },
  { name: "Mint Green", hex: "#3EFFB0" },
  { name: "Yellow", hex: "#FFE14C" },
  { name: "Orange", hex: "#FFB347" },
  { name: "Red", hex: "#FF3D3D" },
];

const SIZES = [
  { key: "50x70", label: "50×70", cm: "50 × 70 cm", price: 145, popular: false, buyable: true },
  { key: "65x90", label: "65×90", cm: "65 × 90 cm", price: 175, popular: true, buyable: true },
  { key: "custom", label: "Custom", cm: "Κατά συνεννόηση", price: null, popular: false, buyable: false },
];

const steps = [
  {
    icon: Camera,
    title: "1. Μοιράσου τη φωτογραφία σου",
    desc: "Στείλε μας την εικόνα που αγαπάς — πορτραίτο, τοπίο, αγαπημένη στιγμή, ζώο, ό,τι θες. Ακόμα και απλή σκαναρισμένη ζωγραφιά.",
  },
  {
    icon: Sparkles,
    title: "2. Σχεδιάζουμε το neon της",
    desc: "Είτε μας πεις συγκεκριμένα σημεία, είτε θα προτείνουμε εμείς που θα φωτίσει πιο όμορφα. Επικοινωνούμε μαζί σου για κάθε λεπτομέρεια.",
  },
  {
    icon: Heart,
    title: "3. Το παραδίδουμε στα χέρια σου",
    desc: "Χειροποίητο flex neon πάνω σε πάνελ με τη φωτογραφία σου. Ένα μοναδικό, προσωπικό artwork που φωτίζει πραγματικά τις αναμνήσεις σου.",
  },
];

export const ArtworksPage = () => {
  const navigate = useNavigate();
  const { addItem, setDrawerOpen } = useCart();
  const [mode, setMode] = useState("idea"); // "idea" | "buy"
  const [form, setForm] = useState({
    name: "",
    email: "",
    placement: "suggest",
    placement_notes: "",
    preferred_color: "",
    size: "65x90",
    description: "",
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const selectedSize = SIZES.find((s) => s.key === form.size);
  const canBuy = !!selectedSize?.buyable;

  // when switching to buy mode but size is custom -> snap to default buyable size
  const switchMode = (next) => {
    if (next === "buy" && !canBuy) {
      setForm((f) => ({ ...f, size: "65x90" }));
    }
    setMode(next);
  };

  const handleFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    const all = [...files, ...incoming];
    if (all.length > MAX_FILES) {
      toast.error(`Έως ${MAX_FILES} αρχεία επιτρέπονται.`);
      return;
    }
    for (const f of incoming) {
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`Η εικόνα "${f.name}" υπερβαίνει τα ${MAX_FILE_MB}MB.`);
        return;
      }
      if (!f.type.startsWith("image/")) {
        toast.error(`"${f.name}": μόνο εικόνες.`);
        return;
      }
    }
    setFiles(all);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Συμπληρώστε όνομα και email.");
      return;
    }
    if (files.length === 0) {
      toast.error("Ανεβάστε τουλάχιστον μία φωτογραφία.");
      return;
    }
    if (form.placement === "custom" && !form.placement_notes.trim()) {
      toast.error("Περιγράψτε πού θέλετε το neon.");
      return;
    }
    if (mode === "buy" && !canBuy) {
      toast.error("Για άμεση αγορά διάλεξε ένα από τα διαθέσιμα μεγέθη (50×70 ή 65×90).");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("placement", form.placement);
      fd.append("placement_notes", form.placement_notes);
      fd.append("preferred_color", form.preferred_color);
      fd.append("size", form.size);
      fd.append("description", form.description);
      fd.append("mode", mode);
      files.forEach((f) => fd.append("files", f));

      const { data } = await axios.post(`${API}/artworks`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (mode === "buy" && data?.product) {
        addItem(data.product, 1);
        setDrawerOpen(false);
        toast.success("Προστέθηκε στο καλάθι! Συνέχισε στην ολοκλήρωση παραγγελίας.");
        navigate("/checkout");
        return;
      }

      toast.success("Η φωτογραφία σου εστάλη! Θα σε ενημερώσουμε σύντομα.");
      setForm({ name: "", email: "", placement: "suggest", placement_notes: "", preferred_color: "", size: "65x90", description: "" });
      setFiles([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Αποτυχία αποστολής.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen" data-testid="artworks-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 neon-grid-bg opacity-30 pointer-events-none" />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 30% 0%, rgba(255, 62, 165, 0.14), transparent 60%), radial-gradient(ellipse 60% 50% at 80% 100%, rgba(176, 38, 255, 0.14), transparent 60%)",
          }}
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#B026FF]/40 bg-[#B026FF]/5 mb-6">
            <ImageIcon size={14} className="text-[#B026FF]" />
            <span className="text-xs tracking-[0.2em] uppercase text-[#B026FF] font-bold">Artworks</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
            Φώτισε τις <span className="text-[#B026FF] neon-glow-pink">αναμνήσεις</span> σου
          </h1>
          <p className="text-zinc-300 mt-6 max-w-2xl mx-auto leading-relaxed text-lg">
            Έχεις μια αγαπημένη σου φωτογραφία;
            <br />
            Μια στιγμή, μια φιγούρα, έναν τόπο, έναν άνθρωπο που λατρεύεις;
          </p>
          <p className="text-zinc-400 mt-4 max-w-2xl mx-auto leading-relaxed">
            Στείλε μας την εικόνα και θα την κάνουμε να φωτίσει: προσθέτουμε
            flex <span className="text-[#B026FF]">neon</span> στα σημεία που τους αξίζουν —
            είτε μας πεις <strong className="text-white">εσύ</strong> πού, είτε σου{" "}
            <strong className="text-white">προτείνουμε εμείς</strong> με μεράκι &amp; αισθητική.
          </p>
        </div>
      </section>

      {/* Example showcase */}
      <section className="py-14 md:py-16 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <div className="text-xs tracking-[0.3em] uppercase text-[#B026FF] font-bold mb-3">
                Παράδειγμα
              </div>
              <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight mb-4">
                Έτσι <span className="text-[#B026FF] neon-glow-pink">φωτίζεται</span> μια εικόνα
              </h2>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Ο Einstein σε pop-art στυλ — δύο neon accents πάνω στον καμβά:
                ένα <span className="text-[#FFB347]">πορτοκαλί/κίτρινο</span> περίγραμμα
                πίτσας στο μέτωπο και μία <span className="text-[#FF3EA5]">ροζ</span> γλώσσα.
                Ζωντανεύει η εικόνα στον τοίχο με πραγματικό neon που λάμπει.
              </p>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-3 py-1.5 rounded-full border border-white/10 text-zinc-300">
                  📐 2 neon στοιχεία
                </span>
                <span className="px-3 py-1.5 rounded-full border border-white/10 text-zinc-300">
                  🎨 Πολυχρωμία
                </span>
                <span className="px-3 py-1.5 rounded-full border border-white/10 text-zinc-300">
                  ⚡ Dual-color combo
                </span>
              </div>
              <p className="text-zinc-500 text-sm mt-6 italic">
                Δικό σου artwork + λίγο δικό μας μεράκι = μοναδικό τελικό αποτέλεσμα.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="order-1 lg:order-2 relative"
            >
              <div
                className="absolute -inset-6 rounded-3xl blur-3xl opacity-60 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(ellipse 60% 50% at 30% 40%, rgba(255,179,71,0.35), transparent 70%), radial-gradient(ellipse 50% 40% at 70% 70%, rgba(255,62,165,0.35), transparent 70%)",
                }}
              />
              <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-black">
                <img
                  src="https://customer-assets.emergentagent.com/job_gkneon-preview/artifacts/5ugdd4wj_6974b948-78cd-4e85-b61a-9d52f61390a7.png"
                  alt="Παράδειγμα artwork: Einstein pop art με neon accents"
                  className="w-full h-auto object-cover"
                  data-testid="artworks-example-image"
                />
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-md border border-white/10 text-[10px] tracking-[0.2em] uppercase text-[#B026FF] font-bold">
                  Sample
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3 text-center">
            Πώς δουλεύει
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight text-center mb-14">
            Τρία απλά βήματα
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="p-8 rounded-2xl border border-white/5 bg-[#050505] hover:border-[#B026FF]/30 transition-colors"
                data-testid={`artworks-step-${i}`}
              >
                <div className="inline-flex p-3 rounded-xl bg-[#B026FF]/10 text-[#B026FF] mb-5">
                  <s.icon size={22} strokeWidth={1.5} />
                </div>
                <div className="font-heading font-bold text-lg mb-2">{s.title}</div>
                <div className="text-sm text-zinc-400 leading-relaxed">{s.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sizes & Pricing */}
      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">
              Μεγέθη &amp; Τιμές
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight">
              Διάλεξε <span className="text-[#B026FF] neon-glow-pink">μέγεθος</span>
            </h2>
            <p className="text-zinc-400 mt-4 max-w-xl mx-auto">
              Δουλεύουμε σε δύο βασικά μεγέθη — για οτιδήποτε άλλο,{" "}
              <span className="text-white">συνεννοούμαστε μαζί</span>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {SIZES.map((s, i) => (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`relative p-6 rounded-2xl border transition-all ${
                  s.popular
                    ? "border-[#B026FF]/60 bg-[#B026FF]/5 shadow-[0_0_30px_rgba(176,38,255,0.18)]"
                    : "border-white/10 bg-[#050505]"
                }`}
                data-testid={`size-card-${s.key}`}
              >
                {s.popular && (
                  <div className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-[#B026FF] text-black text-[10px] font-bold tracking-wider">
                    POPULAR
                  </div>
                )}
                <div className="flex items-baseline justify-between mb-2">
                  <div className="font-heading font-black text-3xl">
                    {s.label}
                  </div>
                  <div>
                    {s.price !== null ? (
                      <span className="text-[#00FFFF] font-heading font-bold text-2xl">{s.price}€</span>
                    ) : (
                      <span className="text-zinc-400 text-sm">Συνεννόηση</span>
                    )}
                  </div>
                </div>
                <div className="text-xs text-zinc-500 tracking-wider uppercase mb-4">{s.cm}</div>
                {s.key !== "custom" ? (
                  <ul className="text-sm text-zinc-400 space-y-1.5">
                    <li>✓ Χειροποίητο flex neon</li>
                    <li>✓ Printed canvas + wooden panel</li>
                    <li>✓ Παράδοση 2-5 εργάσιμες</li>
                  </ul>
                ) : (
                  <ul className="text-sm text-zinc-400 space-y-1.5">
                    <li>✓ Οποιοδήποτε custom μέγεθος</li>
                    <li>✓ Personalized προσφορά</li>
                    <li>✓ Απευθείας συνεννόηση μαζί μας</li>
                  </ul>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-xs tracking-[0.3em] uppercase text-zinc-500 font-bold mb-3">
            Στείλε μας τη φωτογραφία σου
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tight mb-8">
            Ξεκίνα το δικό σου <span className="text-[#B026FF]">artwork</span>
          </h2>

          {/* Mode switcher */}
          <div
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6"
            data-testid="artworks-mode-switch"
          >
            <ModeOption
              selected={mode === "idea"}
              onSelect={() => switchMode("idea")}
              icon={MessageCircle}
              title="Στείλε μας την ιδέα σου"
              desc="Δωρεάν εκτίμηση — απαντάμε εντός 24 ωρών"
              testid="artworks-mode-idea"
            />
            <ModeOption
              selected={mode === "buy"}
              onSelect={() => switchMode("buy")}
              disabled={!canBuy}
              disabledHint="Διαθέσιμο μόνο για 50×70 ή 65×90"
              icon={ShoppingBag}
              title="Αγορά τώρα"
              desc={
                canBuy
                  ? `Έτοιμη ιδέα; Πλήρωσε άμεσα — ${selectedSize?.price}€`
                  : "Διαθέσιμο μόνο για 50×70 ή 65×90"
              }
              testid="artworks-mode-buy"
            />
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-[#050505] border border-white/5 rounded-2xl p-6 md:p-8 flex flex-col gap-5"
            data-testid="artworks-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Όνομα" required>
                <input value={form.name} onChange={update("name")} className="ainput" required data-testid="artworks-input-name" />
              </Field>
              <Field label="Email" required>
                <input type="email" value={form.email} onChange={update("email")} className="ainput" required data-testid="artworks-input-email" />
              </Field>
            </div>

            {/* Photos */}
            <Field label="Φωτογραφίες" required>
              <label
                className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-white/10 rounded-xl p-8 cursor-pointer hover:border-[#B026FF]/50 hover:bg-white/[0.02] transition-all"
                data-testid="artworks-dropzone"
              >
                <Upload size={28} className="text-[#B026FF]" />
                <div className="text-center">
                  <div className="text-sm font-medium">Κάνε κλικ ή σύρε εικόνες εδώ</div>
                  <div className="text-xs text-zinc-500 mt-1">Έως {MAX_FILES} εικόνες · max {MAX_FILE_MB}MB η κάθε μία</div>
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handleFiles}
                  data-testid="artworks-file-input"
                />
              </label>

              {files.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                  {files.map((f, i) => {
                    const url = URL.createObjectURL(f);
                    return (
                      <div
                        key={i}
                        className="relative group rounded-lg overflow-hidden border border-white/10 bg-[#0A0A0A]"
                        data-testid={`artworks-file-${i}`}
                      >
                        <img src={url} alt={f.name} className="w-full h-28 object-cover" onLoad={() => URL.revokeObjectURL(url)} />
                        <button
                          type="button"
                          onClick={() => removeFile(i)}
                          className="absolute top-1 right-1 bg-black/70 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity text-zinc-300 hover:text-[#B026FF]"
                          aria-label="Αφαίρεση"
                        >
                          <X size={14} />
                        </button>
                        <div className="px-2 py-1 text-[10px] truncate text-zinc-400">{f.name}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Field>

            {/* Placement */}
            <Field label="Πού θες να μπει το neon;" required>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <PlacementOption
                  selected={form.placement === "suggest"}
                  onSelect={() => setForm({ ...form, placement: "suggest" })}
                  icon={Sparkles}
                  title="Προτείνετέ μου"
                  desc="Διαλέξτε εσείς τα καλύτερα σημεία"
                  testid="artworks-placement-suggest"
                />
                <PlacementOption
                  selected={form.placement === "custom"}
                  onSelect={() => setForm({ ...form, placement: "custom" })}
                  icon={MapPin}
                  title="Έχω συγκεκριμένες ιδέες"
                  desc="Θα περιγράψω πού θέλω το neon"
                  testid="artworks-placement-custom"
                />
              </div>
            </Field>

            {form.placement === "custom" && (
              <Field label="Περιέγραψε τα σημεία που θες" required>
                <textarea
                  rows={3}
                  value={form.placement_notes}
                  onChange={update("placement_notes")}
                  className="ainput resize-none"
                  placeholder="π.χ. Ένα neon περίγραμμα στο πρόσωπο, ένα μικρό αστέρι πάνω δεξιά..."
                  required
                  data-testid="artworks-input-placement-notes"
                />
              </Field>
            )}

            {/* Size */}
            <Field label="Μέγεθος" required>
              <div className="grid grid-cols-3 gap-3" data-testid="artworks-sizes">
                {SIZES.map((s) => {
                  const selected = form.size === s.key;
                  const disabledForBuy = mode === "buy" && !s.buyable;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => {
                        if (disabledForBuy) return;
                        setForm({ ...form, size: s.key });
                      }}
                      disabled={disabledForBuy}
                      className={`relative p-3 rounded-xl border text-left transition-all ${
                        selected
                          ? "border-[#B026FF] bg-[#B026FF]/10"
                          : "border-white/10 hover:border-white/25"
                      } ${disabledForBuy ? "opacity-40 cursor-not-allowed hover:border-white/10" : ""}`}
                      data-testid={`artworks-size-${s.key}`}
                      title={disabledForBuy ? "Διαθέσιμο μόνο μέσω «Στείλε την ιδέα σου»" : undefined}
                    >
                      {s.popular && (
                        <div className="absolute -top-2 right-2 px-2 py-0.5 rounded-full bg-[#B026FF] text-black text-[9px] font-bold">
                          POPULAR
                        </div>
                      )}
                      <div className="font-heading font-bold text-base">{s.label}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5">{s.cm}</div>
                      <div className="mt-2">
                        {s.price !== null ? (
                          <span className="text-[#00FFFF] font-heading font-bold">{s.price}€</span>
                        ) : (
                          <span className="text-zinc-400 text-xs">Συνεννόηση</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              {mode === "buy" && (
                <div className="mt-2 text-[11px] text-zinc-500">
                  Για άμεση αγορά διαθέσιμα μόνο τα 50×70 &amp; 65×90.
                </div>
              )}
            </Field>

            {/* Color */}
            <Field label="Προτιμώμενο χρώμα neon (προαιρετικό)">
              <div className="flex flex-wrap gap-3 items-center" data-testid="artworks-colors">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, preferred_color: "" })}
                  className={`px-3 py-2 rounded-full text-xs border transition-all ${
                    form.preferred_color === ""
                      ? "border-[#B026FF] bg-[#B026FF]/10 text-white"
                      : "border-white/10 text-zinc-400 hover:border-white/30"
                  }`}
                >
                  Ας αποφασίσουμε μαζί
                </button>
                {COLORS.map((c) => {
                  const selected = form.preferred_color === c.name;
                  return (
                    <button
                      key={c.name}
                      type="button"
                      onClick={() => setForm({ ...form, preferred_color: c.name })}
                      className={`group inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full text-xs border transition-all ${
                        selected
                          ? "border-[#B026FF] bg-[#B026FF]/10 text-white"
                          : "border-white/10 text-zinc-300 hover:border-white/30"
                      }`}
                      title={c.name}
                      data-testid={`artworks-color-${c.name}`}
                    >
                      <span
                        className="w-6 h-6 rounded-full border border-white/10 flex-shrink-0"
                        style={{
                          background: c.hex,
                          boxShadow: selected
                            ? `0 0 0 2px ${c.hex}, 0 0 16px ${c.hex}aa`
                            : `0 0 8px ${c.hex}77`,
                        }}
                      />
                      <span>{c.name}</span>
                    </button>
                  );
                })}
              </div>
              {form.preferred_color && (
                <div className="mt-2 text-xs text-zinc-500">
                  Επιλεγμένο:{" "}
                  <span
                    style={{
                      color: COLORS.find((c) => c.name === form.preferred_color)?.hex,
                      textShadow: `0 0 8px ${COLORS.find((c) => c.name === form.preferred_color)?.hex}`,
                    }}
                  >
                    {form.preferred_color}
                  </span>
                </div>
              )}
            </Field>

            {/* Description */}
            <Field label="Μοιράσου μαζί μας την ιστορία ή το συναίσθημα πίσω από τη φωτογραφία (προαιρετικό)">
              <textarea
                rows={4}
                value={form.description}
                onChange={update("description")}
                className="ainput resize-none"
                placeholder="Πες μας λίγα λόγια για τη στιγμή, το πρόσωπο ή το συναίσθημα που θες να φωτίσει..."
                data-testid="artworks-input-description"
              />
            </Field>

            {mode === "buy" && canBuy && (
              <div className="flex items-center justify-between gap-3 p-4 rounded-xl border border-[#00FFFF]/30 bg-[#00FFFF]/5">
                <div className="flex items-center gap-3">
                  <CheckCircle2 size={20} className="text-[#00FFFF]" />
                  <div>
                    <div className="text-sm font-semibold text-white">Άμεση αγορά · {selectedSize.label}</div>
                    <div className="text-xs text-zinc-400">Θα μεταφερθείς στην ολοκλήρωση παραγγελίας.</div>
                  </div>
                </div>
                <div className="font-heading font-black text-2xl text-[#00FFFF]" data-testid="artworks-buy-price">
                  {selectedSize.price}€
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className={`mt-2 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg font-bold transition-all disabled:opacity-40 ${
                mode === "buy"
                  ? "bg-[#00FFFF] text-black hover:shadow-[0_0_30px_rgba(0,255,255,0.4)]"
                  : "bg-[#B026FF] text-black hover:neon-box-glow-pink"
              }`}
              data-testid="artworks-submit"
            >
              {submitting ? (
                "Παρακαλώ..."
              ) : mode === "buy" ? (
                <><Lock size={16} /> Συνέχεια στην πληρωμή · {selectedSize?.price}€</>
              ) : (
                <><Send size={16} /> Στείλε το artwork σου</>
              )}
            </button>

            <p className="text-[11px] text-zinc-500 text-center">
              {mode === "buy" ? (
                <>✓ Ασφαλής πληρωμή Stripe ή αντικαταβολή · ✓ Παράδοση 2-5 εργάσιμες · ✓ Επιστροφή 7 ημερών</>
              ) : (
                <>✓ Απαντάμε εντός 24 ωρών · ✓ Χωρίς χρέωση για την εκτίμηση · ✓ Πλήρης εχεμύθεια</>
              )}
            </p>
          </form>
        </div>
      </section>

      <style>{`
        .ainput {
          width: 100%;
          background: #0A0A0A;
          border: 1px solid rgba(255,255,255,0.08);
          color: white;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          font-size: 0.9rem;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .ainput:focus {
          border-color: #B026FF;
          box-shadow: 0 0 0 1px #B026FF;
        }
      `}</style>
    </main>
  );
};

const Field = ({ label, required, children }) => (
  <label className="flex flex-col gap-2">
    <span className="text-xs text-zinc-400 font-medium">
      {label} {required && <span className="text-[#B026FF]">*</span>}
    </span>
    {children}
  </label>
);

const PlacementOption = ({ selected, onSelect, icon: Icon, title, desc, testid }) => (
  <button
    type="button"
    onClick={onSelect}
    className={`flex items-start gap-4 p-4 rounded-xl border text-left transition-all ${
      selected
        ? "border-[#B026FF] bg-[#B026FF]/5 shadow-[0_0_20px_rgba(176,38,255,0.15)]"
        : "border-white/5 hover:border-white/20"
    }`}
    data-testid={testid}
  >
    <div className={`p-2 rounded-lg ${selected ? "bg-[#B026FF] text-black" : "bg-white/5 text-zinc-300"}`}>
      <Icon size={16} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-xs text-zinc-500 mt-1">{desc}</div>
    </div>
  </button>
);

const ModeOption = ({ selected, onSelect, icon: Icon, title, desc, testid, disabled, disabledHint }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onSelect}
    disabled={disabled}
    title={disabled ? disabledHint : undefined}
    className={`flex items-start gap-4 p-4 rounded-xl border text-left transition-all ${
      selected
        ? "border-[#00FFFF] bg-[#00FFFF]/5 shadow-[0_0_20px_rgba(0,255,255,0.15)]"
        : "border-white/5 hover:border-white/20"
    } ${disabled ? "opacity-40 cursor-not-allowed hover:border-white/5" : ""}`}
    data-testid={testid}
  >
    <div className={`p-2 rounded-lg ${selected ? "bg-[#00FFFF] text-black" : "bg-white/5 text-zinc-300"}`}>
      <Icon size={16} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-sm">{title}</div>
      <div className="text-xs text-zinc-500 mt-1">{desc}</div>
    </div>
  </button>
);

export default ArtworksPage;
